import { Component }              from '@angular/core';
import { Title }                  from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';

import { AuthService }            from '../auth/auth.service';
import { ConfigService }          from '../spyglass-search/config.service';
import { OauthService }           from '../auth/oauth2-service';
import { ConfigResponse }         from '../spyglass-search/shared/config-response';

import { setIdp, setRedirect }    from '../shared/oidc-store';

declare const google: any;  // Google GSI

type Idp = string;  // keys from cfg.oidcConfig

interface ProviderCfg {
  clientId:    string;
  redirectUri: string;
  scope?:      string;
}

@Component({
  selector   : 'app-login',
  templateUrl: './login.component.html',
  styleUrls  : ['./login.component.scss'],
  standalone : false
})
export class LoginComponent {
  username           = '';
  password           = '';
  error_message      = '';
  forgotPasswordLink = '';
  fromChat           = false;

  // drives *ngFor in the template
  oidcList: Idp[] = [];

  // cache the fetched config
  private cfg: ConfigResponse | null = null;

  constructor(
    private title  : Title,
    private router : Router,
    private route  : ActivatedRoute,
    private auth   : AuthService,
    private oauth  : OauthService,
    private cfgSvc : ConfigService
  ){
    this.title.setTitle('SWIRL Search');
  }

  ngOnInit(): void {
    // grab “return to chat” flag
    this.route.queryParams.subscribe(p => {
      this.fromChat = p['fromComponent'] === 'chat';
      if (p['oidc_error']) this.error_message = p['oidc_error'];
    });

    // one-shot IdP config fetch
    this.cfgSvc.getConfig('default').subscribe(cfg => {
      this.cfg = cfg;
      const provs = cfg?.oidcConfig ?? {};
      this.oidcList = Object
                      .entries(provs)
                      .filter(([_, p]: any) => p.active)
                      .map(([name]) => name as Idp);

      if (cfg?.swirlBaseURL) {
        this.forgotPasswordLink = `${cfg.swirlBaseURL}/password_reset`;
      }
    });
  }

  // manual username/password
  login(): void {
    this.error_message = '';
    this.auth.login(this.username, this.password).subscribe(
      (data: any) => {
        if (data.token) {
          this.auth.setToken(data.token);
          this.auth.setUser (data.user);
          if (data.chat_view)          this.auth.setChatStatus(data.chat_view === 'True');
          if (data.ai_provider_exists) this.auth.setAIProviderStatus(data.ai_provider_exists === 'True');
          if (data.prompts_exists)     this.auth.setPromptsStatus(data.prompts_exists === 'True');
          if (data.rag_rating_enabled)     this.auth.setRagRatingStatus(data.rag_rating_enabled === 'True');
          if (data.bulk_oauth2_enabled)     this.auth.setBulkOAuth2Status(data.bulk_oauth2_enabled === 'True');
          if (data.confidence_mixer)   this.auth.setConfidenceMixerStatus(data.confidence_mixer === 'True');
          const dest = (this.fromChat && this.auth.getChatStatus()) ? '/chat' : '/';
          this.router.navigate([dest]);
        } else {
          this.error_message = 'Your Username OR Password is incorrect.';
        }
      },
      (err: any) => {
        if (err.status === 403) {
          this.auth.clearUser();
          localStorage.removeItem('swirl-token');
          sessionStorage.clear();
          this.router.navigate(['/lockout']);
        } else {
          console.error(err);
        }
      }
    );
  }

  //unified click handler
  startOidc(idp: Idp): void {
    if (this.fromChat) localStorage.setItem('fromComponent','chat');
    const cfg = this.getProviderCfg(idp);
    if (!cfg) {
      console.error(`${idp} OIDC not configured`);
      return;
    }
    setIdp(idp);
    setRedirect(cfg.redirectUri);

    if (idp === 'Google') {
      google.accounts.oauth2
            .initCodeClient({
              client_id   : cfg.clientId,
              scope       : cfg.scope!,
              ux_mode     : 'redirect',
              redirect_uri: cfg.redirectUri
            })
            .requestCode();
    } else {
      this.oauth.initCodeFlow();  // angular-oauth2-oidc
    }
  }

  // Centralized provider lookup
  private getProviderCfg(idp: string): ProviderCfg | null {
    const prov = this.cfg?.oidcConfig?.[idp];
    if (!prov || !prov.active) return null;
    return {
      clientId   : prov.clientId,
      redirectUri: prov.redirectUri,
      scope      : prov.scope ?? 'openid email profile'
    };
  }
}
